/**
 * Copy of libs/sentry-config/src/scrub-url.ts (86bc02ugy). This legacy CRA app
 * cannot import libs/ until its Vite migration (react-scripts cannot compile
 * them), so the scrubber lives here too. Keep the two in sync.
 *
 * Redacts every query value (a GCS signed URL loses its whole query), drops the fragment, and strips `user:password@`,
 * so credentials such as the `/verify-email?token=` value never reach Sentry.
 */

const REDACTED = '[redacted]';

/**
 * Drop `user:password@` from an absolute or protocol-relative URL's authority.
 * Scoped to the authority: an `@` later in the URL is ordinary data.
 */
const stripUserinfo = (url: string): string => {
  const schemeEnd = url.indexOf('://');
  const authorityStart =
    schemeEnd !== -1 ? schemeEnd + 3 : url.startsWith('//') ? 2 : -1;

  if (authorityStart === -1) {
    return url;
  }
  const authorityEnd = url.slice(authorityStart).search(/[/?#]/);
  const end = authorityEnd === -1 ? url.length : authorityStart + authorityEnd;

  const at = url.slice(authorityStart, end).lastIndexOf('@');

  return at === -1
    ? url
    : url.slice(0, authorityStart) + url.slice(authorityStart + at + 1);
};

// GCS signed URLs: V4 `X-Goog-*` and V2 `GoogleAccessId` / `Signature`. The
// whole query is dropped, keys included (86bc4zqgv).
const SIGNED_URL_KEY = /^(x-goog-|googleaccessid$|signature$)/i;

export const scrubUrl = (url: string): string => {
  // The fragment is dropped, not redacted: nothing here routes on it, and it is
  // where `#mercury_token=` lives.
  const [beforeHash] = stripUserinfo(url).split('#');
  const separator = beforeHash.indexOf('?');

  if (separator === -1) {
    return beforeHash;
  }

  const path = beforeHash.slice(0, separator);
  const keys = beforeHash
    .slice(separator + 1)
    .split('&')
    .filter(Boolean)
    .map((pair) => pair.split('=')[0]);

  if (keys.some((key) => SIGNED_URL_KEY.test(key))) {
    return path;
  }
  return keys.length
    ? `${path}?${keys.map((key) => `${key}=${REDACTED}`).join('&')}`
    : path;
};

/** Scrubs the URL fields Sentry puts on navigation and fetch/xhr breadcrumbs. */
export const scrubBreadcrumbUrls = <T extends { data?: unknown }>(
  breadcrumb: T
): T => {
  const data = breadcrumb.data as Record<string, unknown> | undefined;

  if (data) {
    for (const key of ['url', 'from', 'to']) {
      const value = data[key];
      if (typeof value === 'string') {
        data[key] = scrubUrl(value);
      }
    }
  }

  return breadcrumb;
};

/** Scrubs `request.url` on an error or transaction event. */
export const scrubRequestUrl = <T extends { request?: { url?: string } }>(
  event: T
): T => {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  return event;
};

const SPAN_URL_KEYS = ['url', 'http.url', 'url.full'];

/**
 * Scrubs the URLs on fetch/XHR and resource spans. With tracing on, every
 * request becomes an `http.client` span carrying the full URL in `description`
 * and `data`. Uploads PUT to GCS V4 signed URLs, whose `X-Goog-*` query values
 * are temporary storage credentials, and Sentry's own normalization leaves
 * them in `data['http.url']`.
 */
export const scrubSpan = <
  T extends {
    op?: string;
    description?: string;
    data?: Record<string, unknown>;
  },
>(
  span: T
): T => {
  // A pageload transaction's own timing spans — `browser.DNS`, `browser.TLS/SSL`,
  // `browser.request`, `browser.cache`, `browser.loadEvent` and the rest — carry
  // the whole page URL in `description`, fragment included. The op allowlist
  // skipped them, so `/verify-email?token=` still reached Sentry after the first
  // pass (86bc02ugy). Match on the description instead, and keep the op test for
  // request spans whose description is a bare path.
  const isRequestSpan =
    span.op?.startsWith('http') || span.op?.startsWith('resource');
  if (span.description && (isRequestSpan || span.description.includes('://'))) {
    span.description = scrubUrl(span.description);
  }
  if (span.data) {
    for (const key of SPAN_URL_KEYS) {
      const value = span.data[key];
      if (typeof value === 'string') {
        span.data[key] = scrubUrl(value);
      }
    }
    delete span.data['http.query'];
    delete span.data['http.fragment'];
  }
  return span;
};

/**
 * Scrubs Replay's own recording events. Replay records every resource and
 * navigation performance entry with its full URL and never passes them through
 * `beforeSendSpan`, so GCS signed URLs reached replays unscrubbed, and the
 * navigation entry keeps the original `/verify-email?token=` document URL after
 * the address-bar strip (86bc02ugy).
 */
export const scrubRecordingEvent = <T extends { data?: unknown }>(
  event: T
): T => {
  const data = event.data as { tag?: unknown; payload?: unknown } | undefined;
  if (
    data?.tag === 'performanceSpan' &&
    data.payload &&
    typeof data.payload === 'object'
  ) {
    scrubSpan(
      data.payload as {
        op?: string;
        description?: string;
        data?: Record<string, unknown>;
      }
    );
  }
  if (
    data?.tag === 'breadcrumb' &&
    data.payload &&
    typeof data.payload === 'object'
  ) {
    scrubBreadcrumbUrls(data.payload as { data?: unknown });
  }
  return event;
};
